/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { Directive, Input, ElementRef, Renderer2, SimpleChanges, OnChanges } from '@angular/core';

/**
 * A directive for applying a background image onto the element it's installed.
 *
 * @Input imageUrl: accepts data that conforms to the url() CSS function such as relative/absolute URL, data URL, etc
 * Usage:
 * <div nvHigBackgroundImageOverlay [imageUrl]="imagePath/base64-data"></div>
 ** Sets an image as a background
 **/
@Directive({
    selector: '[nvHigBackgroundImageOverlay]',
    standalone: true
})
export class NvHigBackgroundImageOverlayDirective implements OnChanges {
    @Input() imageUrl: string;

    @Input('nvHigBackgroundImageOverlay') get enabled() {
        return this._enabled;
    }
    set enabled(value: any) {
        this._enabled = coerceBooleanProperty(value);
    }

    private _enabled: boolean;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2
    ) {}

    private setBackgroundStyles() {
        this.renderer.setStyle(
            this.el.nativeElement,
            'background',
            `url("${this.imageUrl}") 0% 0% / cover no-repeat black`
        );
    }

    private removeBackgroundStyles() {
        ['background', 'background-color', 'background-repeat', 'background-size'].forEach(attr => {
            this.renderer.removeStyle(this.el.nativeElement, attr);
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (
            changes.imageUrl?.currentValue &&
            changes.imageUrl?.currentValue !== changes.imageUrl?.previousValue &&
            this.enabled
        ) {
            // re-set background when imageUrl changes
            this.setBackgroundStyles();
        }
        if (!changes.imageUrl?.currentValue || !this.enabled) {
            // when imageUrl becomes empty or when enabled becomes false
            this.removeBackgroundStyles();
        }
    }
}
